import { Button } from "../../../components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "../../../components/ui/tooltip";
import { TooltipWrapper } from "../../../components/ui/tooltip-wrapper";
import { Logger } from "@/lib/logger/Logger";
import { useCentralStore } from "../../../store/Central";
import { AbstractInvoiceTable } from "../components/abstract-invoice-table.component";
import { useAccountingStore } from "../../../store/Accounting";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { CustomDateRangePicker } from "../../../components/custom-date-range-picker/custom-date-range-picker.component";
import { CustomDatePicker } from "../../../components/custom-date-picker/custom-date-picker.component";
import { showNotification } from "../../../store/Central/selectors";
import { AccountingZakDialog } from "../components/accounting-zak-dialog.component";
import { isConfigTrue } from "../../organization/subpages/administration/konfigurationseinstellungen.page";
import { TableExportDialog } from "../components/table-export-dialog.component";
import { PaymentDialog } from "../components/payment-dialog.component";
import {
	ExportDataResult,
	useTableExport,
} from "../../../hooks/useTableExport";
import {
	EXPORT_MAX_ROWS,
	ExportHeaderMetadata,
} from "../../../lib/utils/export-utils";
import { SortingState, PaginationState } from "@tanstack/react-table";
import { Search, X, Info } from "lucide-react";
import {
	TableInvoiceRow,
	InvoiceWithPaymentsView,
	TableRowData,
	PaymentDetail,
	TablePaymentRow,
	ProcessedTransaction,
} from "../types/invoice-table.types";
import { InvoiceCancellationDialog } from "../components/invoice-cancellation-dialog.component";
import { Camt54Upload } from "../components/camt54-upload.component";
import { ProcessingResultsDialog } from "../components/processing-results-dialog.component";
import { AccountingRecipientSelector } from "../components/accounting-recipient-selector.component";
import { useInvoiceReminderActions } from "../mahnwesen/hooks/useInvoiceReminderActions";

export const OffenePostenPage: React.FC = () => {
	const organization = useCentralStore((state) => state.organization);
	const organizationId = organization?.id;
	const [loading, setLoading] = useState<boolean>(true);
	const [zakSentTransactions, setZakSentTransactions] = useState<
		ProcessedTransaction[]
	>([]);
	const [zakResultsDialogOpen, setZakResultsDialogOpen] =
		useState<boolean>(false);
	const [zakDialogOpen, setZakDialogOpen] = useState<boolean>(false);
	const [paymentDialogOpen, setPaymentDialogOpen] = useState<boolean>(false);
	const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] =
		useState<TableInvoiceRow | null>(null);
	const [cancellationDialogOpen, setCancellationDialogOpen] =
		useState<boolean>(false);
	const [selectedInvoiceForCancellation, setSelectedInvoiceForCancellation] =
		useState<TableInvoiceRow | null>(null);
	const [cancellationLoading, setCancellationLoading] =
		useState<boolean>(false);

	// Format date for display
	const formatDate = (date: Date) => {
		return date.toLocaleDateString("de-CH", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	};

	const handleCancelInvoice = async (
		reason: string,
		rebookAsAkonto: boolean,
		cancelReminderFees: boolean
	) => {
		if (
			!selectedInvoiceForCancellation ||
			!selectedInvoiceForCancellation.invoice_id
		) {
			Logger.error("No invoice selected for cancellation");
			return;
		}
		setCancellationLoading(true);
		try {
			await cancelInvoice(
				selectedInvoiceForCancellation.invoice_id,
				reason,
				rebookAsAkonto,
				cancelReminderFees
			);
			await handleFetchOpenInvoices();
		} catch (error) {
			Logger.error(error);
		} finally {
			setCancellationLoading(false);
			setCancellationDialogOpen(false);
			setSelectedInvoiceForCancellation(null);
		}
	};
	// Date picker state - null means current open invoices
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [datePickerValue, setDatePickerValue] = useState<Date | null>(null);

	// Invoice date range filter state
	const [invoiceDateRange, setInvoiceDateRange] = useState<SelectionRange>({
		start: null,
		end: null,
	});

	// Table state for TanStack Table
	const [sorting, setSorting] = useState<SortingState>([
		{ id: "invoice_doc_number", desc: true },
	]);
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 100,
	});
	const [globalFilter, setGlobalFilter] = useState<string>("");
	const [totalRowCount, setTotalRowCount] = useState<number>(0);
	const [selectedRecipientId, setSelectedRecipientId] = useState<string>("");
	const requestIdRef = useRef(0); //necessary to prevent race conditions when fetching data

	const {
		openInvoicesData,
		fetchOpenInvoicesData,
		cancelInvoice,
		createPayment,
		recipientsLookup,
	} = useAccountingStore((state) => ({
		openInvoicesData: state.openInvoicesData,
		fetchOpenInvoicesData: state.fetchOpenInvoicesData,
		cancelInvoice: state.cancelInvoice,
		createPayment: state.createPayment,
		recipientsLookup: state.recipientsLookup,
	}));

	const handleFetchOpenInvoices = useCallback(async () => {
		if (!organizationId) {
			Logger.error("No organization id found");
			return;
		}

		const currentRequestId = ++requestIdRef.current;
		setLoading(true);

		const sortBy = sorting[0]?.id || "invoice_doc_number";
		const sortDirection = sorting[0]?.desc ? "desc" : "asc";

		try {
			const result = await fetchOpenInvoicesData(selectedDate, {
				sortBy,
				sortDirection,
				pageIndex: pagination.pageIndex,
				pageSize: pagination.pageSize,
				globalFilter: globalFilter,
				invoiceDateFrom: invoiceDateRange.start,
				invoiceDateTo: invoiceDateRange.end,
				selectedRecipient: selectedRecipientId,
				updateStore: true,
			});

			if (requestIdRef.current === currentRequestId) {
				if (result) {
					setTotalRowCount(result.totalCount);
				} else {
					setTotalRowCount(0);
				}
			}
		} finally {
			if (requestIdRef.current === currentRequestId) {
				setLoading(false);
			}
		}
	}, [
		organizationId,
		fetchOpenInvoicesData,
		selectedDate,
		sorting,
		pagination,
		globalFilter,
		invoiceDateRange,
		selectedRecipientId,
	]);

	const { reminderActions, reminderDialogs } = useInvoiceReminderActions(
		handleFetchOpenInvoices
	);

	const handleDateChange = (date: Date | null) => {
		setSelectedDate(date);
		setDatePickerValue(date);
		// Reset pagination when date changes
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	};

	const handleClearDate = () => {
		setSelectedDate(null);
		setDatePickerValue(null);
		// Reset pagination when clearing date
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	};

	const { exportTable } = useTableExport();

	const headerMetadata: ExportHeaderMetadata = useMemo(() => {
		const sortingLabel = sorting[0]
			? `${
					sorting[0].id === "invoice_doc_number"
						? "Rechnungsnummer"
						: sorting[0].id === "invoice_date"
							? "Rechnungsdatum"
							: sorting[0].id
				} (${sorting[0].desc ? "absteigend" : "aufsteigend"})`
			: undefined;

		const dateRange =
			invoiceDateRange.start || invoiceDateRange.end
				? {
						start: invoiceDateRange.start
							? formatDate(invoiceDateRange.start)
							: "",
						end: invoiceDateRange.end
							? formatDate(invoiceDateRange.end)
							: "",
					}
				: undefined;

		const recipientName = selectedRecipientId
			? recipientsLookup[selectedRecipientId]?.name
			: undefined;

		return {
			exportType: "offene-posten" as const,
			exportTypeLabel: "Offene Posten",
			exportDate: formatDate(new Date()),
			singleDate: selectedDate ? formatDate(selectedDate) : "Aktuell",
			dateRange,
			globalFilter: globalFilter || undefined,
			sorting: sortingLabel,
			recipientFilter: recipientName,
		};
	}, [
		selectedDate,
		sorting,
		globalFilter,
		invoiceDateRange,
		selectedRecipientId,
		recipientsLookup,
	]);

	const fetchExportData = async (): Promise<ExportDataResult> => {
		const currentSortBy = sorting[0]?.id || "invoice_doc_number";
		const currentSortDirection = sorting[0]?.desc ? "desc" : "asc";

		const exportResult = await fetchOpenInvoicesData(selectedDate, {
			sortBy: currentSortBy,
			sortDirection: currentSortDirection,
			pageIndex: 0,
			pageSize: EXPORT_MAX_ROWS,
			globalFilter,
			invoiceDateFrom: invoiceDateRange.start,
			invoiceDateTo: invoiceDateRange.end,
			selectedRecipient: selectedRecipientId,
			updateStore: false,
		});

		return {
			...exportResult,
			data: exportResult.data as TableInvoiceRow[],
		};
	};

	// Handle all filter and table state changes in one effect
	useEffect(() => {
		handleFetchOpenInvoices();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		selectedDate,
		selectedRecipientId,
		invoiceDateRange,
		sorting,
		pagination,
	]);

	// Handle global filter with debounce
	useEffect(() => {
		if (globalFilter === "") return; // Skip on initial mount
		const timeoutId = setTimeout(() => {
			setPagination((prev) =>
				prev.pageIndex === 0
					? { ...prev } // Force new reference to trigger fetch
					: { ...prev, pageIndex: 0 }
			);
		}, 500);

		return () => clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [globalFilter]);

	// Handle sorting changes - reset to page 1 when sorting changes
	const handleSortingChange = (
		updater: SortingState | ((old: SortingState) => SortingState)
	) => {
		setSorting(updater);
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	};

	return (
		<div className="py-6 flex flex-col w-full items-center">
			<div className="w-full max-w-[1920px] px-6">
				{/* Toolbar */}
				<div className="flex items-center justify-between gap-4 py-3 mb-4 border-b border-gray-200">
					{/* Left: Filters */}
					<div className="flex items-center gap-6">
						<TooltipWrapper title="Die Ansicht zeigt alle offenen Rechnungen und Restbeträge (nach aktuellem Stand oder gewähltem Stichtag).">
							<button
								type="button"
								className="text-gray-400 hover:text-gray-600 p-1"
								aria-label="Zweck anzeigen"
							>
								<Info className="h-4 w-4" />
							</button>
						</TooltipWrapper>
						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-500 whitespace-nowrap">
								Zeitraum:
							</span>
							<CustomDateRangePicker
								dateVal={invoiceDateRange}
								handleDateChange={(range) => {
									setInvoiceDateRange(range);
									setPagination((prev) => ({
										...prev,
										pageIndex: 0,
									}));
								}}
							/>
							{(invoiceDateRange.start ||
								invoiceDateRange.end) && (
								<button
									type="button"
									onClick={() => {
										setInvoiceDateRange({
											start: null,
											end: null,
										});
										setPagination((prev) => ({
											...prev,
											pageIndex: 0,
										}));
									}}
									className="text-gray-400 hover:text-gray-600"
									aria-label="Datumsbereich löschen"
								>
									<X size={16} />
								</button>
							)}
						</div>
						<div className="h-8 w-px bg-gray-200" />

						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-500 whitespace-nowrap">
								Stand per:
							</span>
							<CustomDatePicker
								value={datePickerValue}
								onDateChange={handleDateChange}
								placeholder="Aktuell"
								className="w-40"
							/>
							{selectedDate && (
								<button
									type="button"
									onClick={handleClearDate}
									className="text-gray-400 hover:text-gray-600"
									aria-label="Datum löschen"
								>
									<X size={16} />
								</button>
							)}
						</div>
						<div className="h-8 w-px bg-gray-200" />
						<AccountingRecipientSelector
							selectedRecipientId={selectedRecipientId}
							onRecipientChange={setSelectedRecipientId}
							recipientList={recipientsLookup}
						/>
						<div className="h-8 w-px bg-gray-200" />
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
							<input
								type="text"
								placeholder="Suchen..."
								aria-label="Suchen"
								data-testid="offene-posten-search-input"
								value={globalFilter}
								onChange={(e) =>
									setGlobalFilter(e.target.value)
								}
								className="w-64 h-10 pl-10 pr-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
							/>
						</div>
					</div>
					{/* Right: Actions */}
					<div className="flex items-center gap-2">
						<TableExportDialog
							invoiceData={openInvoicesData as TableInvoiceRow[]}
							loading={loading}
							onExport={exportTable}
							fetchExportData={fetchExportData}
							currentSorting={
								sorting[0]
									? `${sorting[0].id} (${
											sorting[0].desc
												? "absteigend"
												: "aufsteigend"
										})`
									: "Rechnungsnummer (absteigend)"
							}
							currentFilters={(() => {
								const filters: string[] = [];
								if (selectedDate)
									filters.push(
										`Stand per: ${formatDate(selectedDate)}`
									);
								if (globalFilter)
									filters.push(`Suche: "${globalFilter}"`);
								if (
									selectedRecipientId &&
									recipientsLookup[selectedRecipientId]
								) {
									filters.push(
										`Empfänger: ${recipientsLookup[selectedRecipientId].name}`
									);
								}
								if (
									invoiceDateRange.start &&
									invoiceDateRange.end
								) {
									filters.push(
										`Rechnungsdatum: ${formatDate(
											invoiceDateRange.start
										)} - ${formatDate(
											invoiceDateRange.end
										)}`
									);
								} else if (invoiceDateRange.start) {
									filters.push(
										`Rechnungsdatum ab: ${formatDate(
											invoiceDateRange.start
										)}`
									);
								} else if (invoiceDateRange.end) {
									filters.push(
										`Rechnungsdatum bis: ${formatDate(
											invoiceDateRange.end
										)}`
									);
								}
								return filters.length > 0
									? filters.join(", ")
									: "Keine Filter aktiv";
							})()}
							headerMetadata={headerMetadata}
							showInvoicePdfsOption={true}
						/>
						{isConfigTrue(organization?.config, "zak_enabled") && (
							<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="secondary"
											onClick={() =>
												setZakDialogOpen(true)
											}
										>
											An ZAK senden
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										Rechnungen an ZAK senden
									</TooltipContent>
							</Tooltip>
						)}
						<Camt54Upload
							accountingData={
								openInvoicesData as TableInvoiceRow[]
							}
							organizationId={organizationId ?? undefined}
							onProcessingComplete={handleFetchOpenInvoices}
							disabled={selectedDate !== null}
						/>
					</div>
				</div>

				<AbstractInvoiceTable<TableRowData>
					data={(() => {
						// Transform invoice data to include payment sub-rows
						if (
							!openInvoicesData ||
							!Array.isArray(openInvoicesData)
						) {
							return [];
						}
						return (openInvoicesData as TableInvoiceRow[])
							.filter(
								(invoice: TableInvoiceRow) => invoice != null
							)
							.map((invoice: TableInvoiceRow) => ({
								...invoice,
								isPaymentRow: false as const,
								subRows: (
									invoice.payments as unknown as TablePaymentRow[]
								)?.map((payment: TablePaymentRow) => ({
									...payment,
									invoice_id: invoice.invoice_id || 0,
									isPaymentRow: true as const,
								})),
							}));
					})()}
					tableType={selectedDate ? "open-items" : "active"}
					loading={loading}
					features={{
						allowSharing: !selectedDate, // Only allow sharing for current open invoices
						allowCancelInvoice: !selectedDate, // Only allow cancellation for current open invoices
						allowPayments: !selectedDate, // Only allow payments for current open invoices
						allowBookAsLoss: !selectedDate, // Only allow loss booking for current open invoices
						readOnlyMode: !!selectedDate, // Historical view is read-only
						historicalView: !!selectedDate,
						allowZak: isConfigTrue(
							organization?.config,
							"zak_enabled"
						),
					}}
					uiCallbacks={{
						onPaymentDialogOpen: (invoice) => {
							if (!selectedDate) {
								setSelectedInvoiceForPayment(invoice);
								setPaymentDialogOpen(true);
							} else {
								showNotification({
									message:
										"Zahlungen können nur für aktuelle offene Posten erfasst werden",
									type: "warning",
								});
							}
						},
						onRefreshData: handleFetchOpenInvoices,
						onCancellationDialogOpen: (invoice) => {
							setSelectedInvoiceForCancellation(invoice);
							setCancellationDialogOpen(true);
						},
						reminderActions: selectedDate
							? undefined
							: reminderActions,
					}}
					// Table state
					sorting={sorting}
					onSortingChange={handleSortingChange}
					pagination={pagination}
					onPaginationChange={setPagination}
					globalFilter={globalFilter}
					onGlobalFilterChange={setGlobalFilter}
					// Data fetching
					totalRowCount={totalRowCount}
					manualSorting={true}
					manualPagination={true}
					manualFiltering={true}
					showVATSummary={true}
					showPaymentSummary="open"
					emptyStateLabel="Keine offenen Posten vorhanden"
				/>
				<AccountingZakDialog
					open={zakDialogOpen}
					onOpenChange={setZakDialogOpen}
					accountingData={openInvoicesData as TableInvoiceRow[]}
					onZakSent={async (invoiceIds: number[]) => {
						await handleFetchOpenInvoices();

						// Get fresh data from store after fetch
						const freshInvoiceData = useAccountingStore.getState()
							.openInvoicesData as TableInvoiceRow[];

						// Build ProcessedTransaction objects for ZAK sent invoices
						const zakTransactions: ProcessedTransaction[] =
							invoiceIds.map((invoiceId) => {
								const invoice = freshInvoiceData.find(
									(inv) => inv.invoice_id === invoiceId
								);

								if (invoice) {
									// Get client display name from invoice
									let clientName = "Unbekannt";
									if (invoice.recipient_is_company) {
										clientName =
											invoice.recipient_company_name ||
											"Unbekannt";
									} else if (
										invoice.recipient_first_name ||
										invoice.recipient_last_name
									) {
										clientName = `${
											invoice.recipient_first_name || ""
										} ${
											invoice.recipient_last_name || ""
										}`.trim();
									}

									return {
										invoice_id: invoiceId,
										invoice_doc_number:
											invoice.invoice_doc_number || null,
										invoice_date:
											invoice.invoice_date || null,
										client_name: clientName,
										amount: invoice.invoice_value || 0,
										currency: "CHF",
										success: true,
									};
								}

								// Fallback for invoices not found in state
								return {
									invoice_id: invoiceId,
									invoice_doc_number: null,
									invoice_date: null,
									client_name: null,
									amount: 0,
									currency: "CHF",
									success: false,
									error: "Rechnung nicht gefunden",
								};
							});

						setZakSentTransactions(zakTransactions);
						setZakResultsDialogOpen(true);
					}}
				/>
				<PaymentDialog
					open={paymentDialogOpen}
					onClose={() => {
						setPaymentDialogOpen(false);
						setSelectedInvoiceForPayment(null);
					}}
					invoice={selectedInvoiceForPayment}
					onSubmit={async (paymentData) => {
						await createPayment(paymentData);
						setPaymentDialogOpen(false);
						setSelectedInvoiceForPayment(null);
						await handleFetchOpenInvoices();
					}}
					loading={false}
				/>
				<InvoiceCancellationDialog
					isOpen={cancellationDialogOpen}
					onClose={() => {
						setCancellationDialogOpen(false);
						setSelectedInvoiceForCancellation(null);
					}}
					onConfirm={handleCancelInvoice}
					invoice={selectedInvoiceForCancellation}
					loading={cancellationLoading}
				/>

				{reminderDialogs}
				<ProcessingResultsDialog
					open={zakResultsDialogOpen}
					onClose={() => setZakResultsDialogOpen(false)}
					transactions={zakSentTransactions}
					mode="zak"
				/>
			</div>
		</div>
	);
};
